import { useTranslation } from 'react-i18next';
import {
  useState,
  forwardRef,
  useImperativeHandle,
  Ref,
  useCallback,
} from 'react';
import { TranslationsKeys } from '../../../../../locales/translations';
import { ReportAsyncForm, ReportAsyncTypeRow } from '../types';
import { Modal, addToast } from '@octano/global-ui';
import { Row, Col } from 'reactstrap';
import DynamicFormBase from '../../../../../components/DynamicForm/DynamicFormBase';
import { generateReport, getReportForm } from '../requests';
import Loading from '../../../../../components/info/Loading';

export type GenerateReportModalMethods = {
  open: (row: ReportAsyncTypeRow) => void;
};
export type GenerateReportModalProps = { onGenerate?: () => void };

const GenerateReportModal = (
  { onGenerate }: GenerateReportModalProps,
  ref: Ref<GenerateReportModalMethods>,
) => {
  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_REPORTS_ASYNC);

  const [loading, setLoading] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [report, setReport] = useState<ReportAsyncTypeRow>();
  const [reportForm, setReportForm] = useState<ReportAsyncForm>();

  const close = useCallback(() => {
    setIsOpen(false);
    setReport(undefined);
    setReportForm(undefined);
  }, []);

  const fetchForm = useCallback(
    async (row: ReportAsyncTypeRow) => {
      if (generating || loading) {
        return;
      }
      setLoading(true);
      try {
        const { data, error } = await getReportForm(row);
        if (data && !error) {
          setReportForm(data);
        } else {
          close();
        }
      } catch (error) {
        close();
      }
      setLoading(false);
    },
    [generating, loading, close],
  );

  const open = useCallback(
    (row: ReportAsyncTypeRow) => {
      setReport(row);
      fetchForm(row);
      setIsOpen(true);
    },
    [fetchForm],
  );

  const handleError = useCallback(
    (error: any) => {
      if (['IS_GENERATING'].includes(error?.data?.message)) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`errors.${error?.data?.message}`),
        });
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: error?.data?.message,
        });
      }
    },
    [t],
  );

  const handleGenerate = useCallback(
    async (values: any) => {
      if (generating || loading || !report) {
        return;
      }
      setGenerating(true);
      const { error } = await generateReport(report, values);
      setGenerating(false);
      if (error) {
        handleError(error);
        return;
      }
      addToast({
        icon: 'success',
        color: 'success',
        text: t('successRequest'),
      });
      close();
      !!onGenerate && onGenerate();
      setGenerating(false);
    },
    [generating, loading, onGenerate, close, report, t, handleError],
  );

  useImperativeHandle(ref, () => ({ open }));

  return (
    <Modal isOpen={isOpen} toggle={() => !generating && close()} unmountOnClose>
      <Row className="text-center">
        <Col xs={12} className="pb-3">
          <span className="fs-22 text-dark">{report?.name || ''}</span>
        </Col>
        <Col xs={12}>
          <p className="fs-16 fw-300">{report?.instructions || ''}</p>
        </Col>
      </Row>
      {!!reportForm?.structure && (
        <DynamicFormBase
          isModalRender
          options={reportForm?.options}
          structure={reportForm?.structure}
          onSubmit={handleGenerate}
          formButtons={[
            {
              type: 'button',
              text: t('common:actions.back'),
              onClick: close,
              disabledInLoading: true,
              disabledInSubmitting: true,
              outlined: true,
              fullwidth: true,
              col: { lg: 6 },
            },
            {
              type: 'submit',
              text: t('actions.generate'),
              disabledInLoading: true,
              outlined: false,
              fullwidth: true,
              col: { lg: 6 },
            },
          ]}
        />
      )}
      {!!loading && <Loading insideCard />}
    </Modal>
  );
};

export default forwardRef(GenerateReportModal);
